/**
 * Cookie Consent Hook
 *
 * Manages cookie consent state with localStorage persistence
 * NO RECURRING BANNERS - saves choice permanently
 */

import { useState, useEffect } from 'react';
import {
  CookiePreferences,
  CookieConsentState,
  DEFAULT_COOKIE_PREFERENCES,
  COOKIE_CONSENT_STORAGE_KEY,
  COOKIE_POLICY_VERSION,
} from './types';

export const useCookieConsent = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [consentState, setConsentState] = useState<CookieConsentState>({
    hasConsented: false,
    preferences: DEFAULT_COOKIE_PREFERENCES,
    needsConsent: true,
  });

  // Load consent from localStorage on mount
  useEffect(() => {
    const loadConsent = () => {
      try {
        const stored = localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);

          // Check if consent is still valid (same policy version)
          if (parsed.version === COOKIE_POLICY_VERSION) {
            setConsentState({
              hasConsented: true,
              preferences: {
                ...parsed,
                timestamp: new Date(parsed.timestamp),
              },
              needsConsent: false,
            });

            // Apply consent choices immediately
            applyConsentChoices(parsed);
          } else {
            // Policy updated - need new consent
            setConsentState({
              hasConsented: false,
              preferences: DEFAULT_COOKIE_PREFERENCES,
              needsConsent: true,
            });
          }
        }
      } catch (error) {
        console.error('Failed to load cookie consent:', error);
      } finally {
        setIsInitialized(true);
      }
    };

    loadConsent();
  }, []);

  const saveConsent = (preferences: CookiePreferences) => {
    const consentData = {
      ...preferences,
      timestamp: new Date().toISOString(),
      version: COOKIE_POLICY_VERSION,
    };

    // Save to localStorage (permanent until cleared)
    localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(consentData));

    // Update state
    setConsentState({
      hasConsented: true,
      preferences: {
        ...preferences,
        timestamp: new Date(),
        version: COOKIE_POLICY_VERSION,
      },
      needsConsent: false,
    });

    // Apply choices immediately
    applyConsentChoices(preferences);

    // TODO: Send to backend for user profile if authenticated
    // This allows users to have same consent across devices
    if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
      saveConsentToBackend(consentData).catch(err =>
        console.warn('Failed to sync consent to backend:', err)
      );
    }
  };

  const acceptAll = () => {
    saveConsent({
      essential: true,
      analytics: true,
      marketing: true,
      preferences: true,
      timestamp: new Date(),
      version: COOKIE_POLICY_VERSION,
    });
  };

  const acceptEssentialOnly = () => {
    saveConsent({
      essential: true,
      analytics: false,
      marketing: false,
      preferences: true, // Keep preferences for better UX
      timestamp: new Date(),
      version: COOKIE_POLICY_VERSION,
    });
  };

  const saveCustomPreferences = (prefs: Partial<CookiePreferences>) => {
    saveConsent({
      essential: true, // Always required
      analytics: prefs.analytics ?? false,
      marketing: prefs.marketing ?? false,
      preferences: prefs.preferences ?? true,
      timestamp: new Date(),
      version: COOKIE_POLICY_VERSION,
    });
  };

  const resetConsent = () => {
    localStorage.removeItem(COOKIE_CONSENT_STORAGE_KEY);
    setConsentState({
      hasConsented: false,
      preferences: DEFAULT_COOKIE_PREFERENCES,
      needsConsent: true,
    });
  };

  return {
    ...consentState,
    isInitialized,
    acceptAll,
    acceptEssentialOnly,
    saveCustomPreferences,
    resetConsent,
  };
};

/**
 * Apply consent choices to third-party services
 */
function applyConsentChoices(preferences: CookiePreferences) {
  // Google Analytics - only load if analytics consent given
  if (preferences.analytics && typeof window !== 'undefined') {
    // @ts-ignore
    if (window.gtag) {
      // @ts-ignore
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted',
      });
    }
  } else if (typeof window !== 'undefined') {
    // @ts-ignore
    if (window.gtag) {
      // @ts-ignore
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied',
      });
    }
  }

  // Marketing cookies
  if (preferences.marketing && typeof window !== 'undefined') {
    // @ts-ignore
    if (window.gtag) {
      // @ts-ignore
      window.gtag('consent', 'update', {
        'ad_storage': 'granted',
        'ad_user_data': 'granted',
        'ad_personalization': 'granted',
      });
    }
  } else if (typeof window !== 'undefined') {
    // @ts-ignore
    if (window.gtag) {
      // @ts-ignore
      window.gtag('consent', 'update', {
        'ad_storage': 'denied',
        'ad_user_data': 'denied',
        'ad_personalization': 'denied',
      });
    }
  }
}

/**
 * Save consent to backend (for authenticated users)
 * Syncs to user preferences for cross-device consistency
 */
async function saveConsentToBackend(consentData: any): Promise<void> {
  try {
    const token = localStorage.getItem('token');
    if (!token) return; // Not authenticated

    // Update user preferences with cookie consent
    await fetch('/api/v2/users/me', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        preferences: {
          cookiePreferences: {
            essential: consentData.essential,
            analytics: consentData.analytics,
            marketing: consentData.marketing,
            preferences: consentData.preferences,
            timestamp: consentData.timestamp,
            version: consentData.version,
          },
        },
      }),
    });
  } catch (error) {
    // Non-critical - localStorage is primary
    console.warn('Failed to save consent to backend:', error);
  }
}
